import { useEffect, useState, type ReactNode } from 'react';
import { requestSync, resetDevice, startEngine } from '../engine/client';
import { cloudCall, cloudMe, loginUrl, logoutUrl, type CloudMe } from './api';
import { createKey, newRecoveryCode, openKeybox, type Keybox } from './crypto';
import { clearKey, keyOwner, loadKey, saveKey } from './keystore';
import { setCloudState } from './state';

type Gate =
  | { step: 'loading' }
  | { step: 'off' }
  | { step: 'login'; me: CloudMe }
  | { step: 'setup'; me: CloudMe; code: string }
  | { step: 'recover'; me: CloudMe }
  | { step: 'switch'; me: CloudMe }
  | { step: 'ready' };

const LOGIN_ERROR: Record<string, string> = {
  invite: '처음 오신 분은 초대 링크가 있어야 가입할 수 있습니다. 초대해 준 사람에게 링크를 받아 그 링크로 다시 들어오세요.',
  cancel: '로그인을 취소했습니다.',
  state: '로그인 창이 너무 오래 열려 있었습니다. 다시 눌러 주세요.',
};

/** 클라우드 사이트로 열었을 때: 로그인 → (처음이면) 복구 코드 만들기 / (새 기기면) 복구 코드 넣기 → 앱.
 *  PC 서버나 Tailscale 주소로 열면 그냥 앱을 보여 준다. */
export function CloudGate({ children }: { children: ReactNode }) {
  const [gate, setGate] = useState<Gate>({ step: 'loading' });

  useEffect(() => {
    void (async () => {
      const me = await cloudMe();
      setCloudState(me);
      if (!me) return setGate({ step: 'off' });
      if (!me.user) return setGate({ step: 'login', me });
      if (await loadKey(me.user.id)) return setGate({ step: 'ready' });
      const owner = await keyOwner();
      if (owner && owner !== me.user.id) return setGate({ step: 'switch', me });
      const box = await cloudCall<Keybox | null>('GET', 'api/cloud/keybox');
      setGate(box ? { step: 'recover', me } : { step: 'setup', me, code: newRecoveryCode() });
    })();
  }, []);

  const ready = () => {
    setGate({ step: 'ready' });
    window.setTimeout(requestSync, 0); // 엔진이 뜬 뒤 첫 동기화 (엔진도 준비되면 스스로 한 번 맞춘다)
  };

  useEffect(() => {
    // 엔진(15MB)은 앱을 실제로 열 때 띄운다 — 로그인 화면에서 먼저 띄우면 로그인 이동 때 창 잠금이 겹친다
    if (gate.step === 'off' || gate.step === 'ready') startEngine();
  }, [gate.step]);

  if (gate.step === 'off' || gate.step === 'ready') return <>{children}</>;
  if (gate.step === 'loading') return <div className="gate"><p className="muted">불러오는 중…</p></div>;
  if (gate.step === 'login') return <Login me={gate.me} />;
  if (gate.step === 'setup') return <Setup me={gate.me} code={gate.code} onDone={ready} onExisting={() => setGate({ step: 'recover', me: gate.me })} />;
  if (gate.step === 'recover') return <Recover me={gate.me} onDone={ready} />;
  return (
    <div className="gate">
      <h1>다른 사람의 기록이 이 기기에 있습니다</h1>
      <p>이 기기에는 다른 계정으로 쓰던 기록이 남아 있습니다. {gate.me.user?.name} 님으로 쓰려면 이 기기의 기록을 지우고 새로 받습니다 (지운 기록은 원래 계정에 클라우드로 보관돼 있습니다).</p>
      <div className="row-gap">
        <button type="button" className="btn btn-primary" onClick={() => void clearKey().then(resetDevice)}>이 기기 기록 지우고 계속</button>
        <a className="btn btn-ghost" href={logoutUrl()}>로그아웃</a>
      </div>
    </div>
  );
}

function Login({ me }: { me: CloudMe }) {
  const params = new URLSearchParams(location.search);
  const [invite, setInvite] = useState(params.get('invite') ?? '');
  const [testName, setTestName] = useState('');
  const err = params.get('login_error');
  const p = me.providers;
  return (
    <div className="gate">
      <div className="gate-brand">▲ APEX</div>
      <h1>로그인</h1>
      <p className="muted">훈련·복습 기록을 폰과 PC 에서 함께 씁니다. 기록은 이 기기에서 잠근 뒤 올라가서, 서버에서는 아무도 내용을 볼 수 없습니다.</p>
      {err && <p className="gate-error" role="alert">{LOGIN_ERROR[err] ?? `로그인하지 못했습니다: ${err}`}</p>}
      <div className="gate-buttons">
        {p.google && <a className="btn big gate-google" href={loginUrl('google', invite)}>구글로 로그인</a>}
        {p.kakao && <a className="btn big gate-kakao" href={loginUrl('kakao', invite)}>카카오로 로그인</a>}
        {!p.google && !p.kakao && !p.test && <p className="muted">로그인 방법을 준비 중입니다.</p>}
      </div>
      <details className="gate-invite" open={Boolean(invite)}>
        <summary>초대 코드가 있어요</summary>
        <input className="input" aria-label="초대 코드" placeholder="초대 코드" value={invite} onChange={(e) => setInvite(e.target.value.trim().toUpperCase())} />
        <p className="small muted">처음 가입할 때만 필요합니다. 초대 링크로 들어왔다면 이미 채워져 있습니다.</p>
      </details>
      {p.test && (
        <div className="gate-test">
          <input className="input" aria-label="시험 이름" placeholder="시험 이름" value={testName} onChange={(e) => setTestName(e.target.value)} />
          <a className="btn" href={loginUrl('test', invite, `&name=${encodeURIComponent(testName || '시험')}`)}>시험 로그인</a>
        </div>
      )}
    </div>
  );
}

function Setup({ me, code, onDone, onExisting }: { me: CloudMe; code: string; onDone: () => void; onExisting: () => void }) {
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const start = async () => {
    setBusy(true);
    setError('');
    try {
      const { key, box } = await createKey(code);
      try {
        await cloudCall('PUT', 'api/cloud/keybox', { box });
      } catch (e) {
        if (String(e).includes('이미 열쇠')) return onExisting(); // 그 사이 다른 기기에서 먼저 만듦
        throw e;
      }
      await saveKey(me.user!.id, key);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="gate">
      <h1>{me.user?.name} 님, 복구 코드를 적어 두세요</h1>
      <p>기록은 이 코드로 잠깁니다. <b>새 폰이나 다른 PC 에서 기록을 열 때 이 코드가 필요합니다.</b> 잃어버리면 서버에 있는 기록은 아무도(운영자도) 열 수 없습니다.</p>
      <div className="gate-code" aria-label="복구 코드">{code}</div>
      <div className="row-gap">
        <button type="button" className="btn" onClick={() => void navigator.clipboard?.writeText(code)}>복사</button>
      </div>
      <label className="check">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        안전한 곳(메모 앱, 종이)에 적어 두었습니다
      </label>
      {error && <p className="gate-error" role="alert">{error}</p>}
      <button type="button" className="btn btn-primary big" disabled={!saved || busy} onClick={() => void start()}>{busy ? '준비 중…' : '시작하기'}</button>
    </div>
  );
}

function Recover({ me, onDone }: { me: CloudMe; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const open = async () => {
    setBusy(true);
    setError('');
    try {
      const box = await cloudCall<Keybox>('GET', 'api/cloud/keybox');
      await saveKey(me.user!.id, await openKeybox(box, code));
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="gate">
      <h1>{me.user?.name} 님, 복구 코드를 넣어 주세요</h1>
      <p className="muted">이 기기에서 처음 여는 계정입니다. 처음 가입할 때 적어 둔 복구 코드를 넣으면 기록을 받아 옵니다.</p>
      <form onSubmit={(e) => { e.preventDefault(); void open(); }} className="gate-form">
        <input className="input gate-code-input" aria-label="복구 코드" placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} />
        <button type="submit" className="btn btn-primary" disabled={busy || code.replace(/[^A-Za-z0-9]/g, '').length < 24}>{busy ? '여는 중…' : '열기'}</button>
      </form>
      {error && <p className="gate-error" role="alert">{error}</p>}
      <a className="small" href={logoutUrl()}>다른 계정으로 로그인</a>
    </div>
  );
}
